/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Subscription Management Page
 * ============================================================================
 *
 * Page for managing an organization's module subscriptions.
 * Allows adding/removing modules, changing tiers, and updating user counts.
 *
 * ============================================================================
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import {
  ArrowLeft,
  Building2,
  Save,
  Loader2,
  Check,
  Plus,
  Trash2,
  Users,
  DollarSign,
  Percent,
  AlertCircle,
  RefreshCw,
  Edit,
  ChevronDown,
  ChevronRight,
  ToggleLeft,
  ToggleRight,
  Puzzle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization, organizationKeys } from '@/hooks/api/useOrganizations';
import {
  useOrgSubscription,
  useCreateSubscription,
  useUpdateSubscription,
  useAddModule,
  useChangeTier,
  useRemoveModule,
  useUpdateModuleUserCount,
  useConvertTrial,
  useToggleModuleFeature,
  subscriptionKeys,
} from '@/hooks/api/useSubscriptions';
import { usePricingTiers, useVolumeDiscounts } from '@/hooks/api/useModulePricing';
import { useCurrencies } from '@/hooks/api/useCurrencies';
// Org-scoped hooks for OrgAdmin access
import {
  useMySubscription,
  useMyCreateSubscription,
  useMyUpdateSubscription,
  useMyAddModule,
  useMyChangeTier,
  useMyRemoveModule,
  useMyUpdateModuleUserCount,
  useMyConvertTrial,
  useMyToggleModuleFeature,
  mySubscriptionKeys,
} from '@/hooks/api/useMySubscription';
import { useOrgProfile } from '@/hooks/api/useOrgProfile';
import { useBillingCatalogTiers, useBillingCatalogDiscounts } from '@/hooks/api/useBillingCatalog';
import { useNotification } from '@/hooks';
import { ModulePricingTier, SubscriptionSummary, ModuleSubscriptionItem } from '@/types/superadmin.types';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import { ConfirmationDialog } from '@/components/ui';
import { AddonsMarketplace } from '@/components/billing';
import { subscriptionsService } from '@/services/superadmin/subscriptions.service';
import { mySubscriptionService } from '@/services/billing/my-subscription.service';
import type { FeatureAddonItem, FeatureImpactDto } from '@/types/addon.types';
import { FeatureToggleConfirmDialog } from '@/components/billing/FeatureToggleConfirmDialog';
import { addonService } from '@/services/billing/addon.service';

// Module display info
const MODULE_INFO: Record<string, { name: string; icon: string; description: string }> = {
  CORE: { name: 'Core Platform', icon: '🏢', description: 'Core platform features (included)' },
  COMPTA: { name: 'Accounting', icon: '📊', description: 'Invoicing, expenses, financial reports' },
  HR: { name: 'Human Resources', icon: '👥', description: 'Employee management, payroll, leave tracking' },
  CRM: { name: 'Customer Relationship', icon: '🤝', description: 'Contacts, deals, sales pipeline' },
  PM: { name: 'Project Management', icon: '📋', description: 'Tasks, projects, time tracking' },
};

const COMING_SOON_MODULES = new Set(['HR', 'COMPTA', 'PM']);

// Backend error message patterns to translation keys
const BACKEND_ERROR_PATTERNS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /already has a subscription/i, key: 'alreadyHasSubscription' },
  { pattern: /Module already subscribed/i, key: 'moduleAlreadySubscribed' },
  { pattern: /Cannot change tier for inactive/i, key: 'cannotChangeTierInactive' },
  { pattern: /exceeds limit for tier/i, key: 'userCountExceedsLimit' },
  { pattern: /other modules depend on it/i, key: 'cannotRemoveDependency' },
  { pattern: /not a per-user module/i, key: 'notPerUserModule' },
  { pattern: /not on trial/i, key: 'moduleNotOnTrial' },
  { pattern: /Missing required dependency/i, key: 'missingDependencies' },
];

// Helper function to translate backend error messages
function translateBackendError(message: string, t: (key: string, fallback?: string) => string): string {
  for (const { pattern, key } of BACKEND_ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return t(`subscriptionManagement.backendErrors.${key}`, message);
    }
  }
  return message;
}

// Module selection for adding new modules
interface ModuleSelectionItem {
  moduleCode: string;
  tierCode: string;
  userCount: number;
  selected: boolean;
}

export default function SubscriptionManagementPage() {
  const { t } = useTranslation('platform');
  const { id: paramId } = useParams();
  const { user } = useAuthStore();
  const id = paramId || user?.orgId;
  const isOrgAdmin = !paramId && user?.role !== 'SUPER_ADMIN';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotification();

  // SuperAdmin hooks (disabled for OrgAdmin via empty string / enabled flag)
  const { data: adminOrg, isLoading: adminOrgLoading } = useOrganization(isOrgAdmin ? '' : (id || ''));
  const { data: adminSub, isLoading: adminSubLoading, refetch: adminRefetch } = useOrgSubscription(isOrgAdmin ? '' : (id || ''));
  const { data: adminTiers = [] } = usePricingTiers(undefined, { enabled: !isOrgAdmin });
  const { data: adminDiscounts = [] } = useVolumeDiscounts({ enabled: !isOrgAdmin });

  // OrgAdmin hooks (disabled for SuperAdmin)
  const { data: orgProfile, isLoading: profileLoading } = useOrgProfile();
  const { data: mySub, isLoading: mySubLoading, refetch: myRefetch } = useMySubscription();
  const { data: catalogTiers = [] } = useBillingCatalogTiers();
  const { data: catalogDiscounts = [] } = useBillingCatalogDiscounts();

  // Unified data — seamless for the rest of the component
  const org = isOrgAdmin
    ? (orgProfile ? { name: orgProfile.name, code: orgProfile.code } : undefined)
    : adminOrg;
  const orgLoading = isOrgAdmin ? profileLoading : adminOrgLoading;
  const subscription = isOrgAdmin ? mySub : adminSub;
  const subLoading = isOrgAdmin ? mySubLoading : adminSubLoading;
  const refetchSubscription = isOrgAdmin ? myRefetch : adminRefetch;
  const pricingTiers = isOrgAdmin ? catalogTiers : adminTiers;
  const volumeDiscounts = isOrgAdmin ? catalogDiscounts : adminDiscounts;

  const { data: currencies = [] } = useCurrencies();

  // Mutations — call all unconditionally (React hooks rule), then pick based on role
  const adminCreateSub = useCreateSubscription();
  const adminUpdateSub = useUpdateSubscription();
  const adminAddMod = useAddModule();
  const adminChangeTier = useChangeTier();
  const adminRemoveMod = useRemoveModule();
  const adminUpdateUC = useUpdateModuleUserCount();
  const adminConvertTrial = useConvertTrial();
  const adminToggleFeat = useToggleModuleFeature();

  const myCreateSub = useMyCreateSubscription();
  const myUpdateSub = useMyUpdateSubscription();
  const myAddMod = useMyAddModule();
  const myChangeTier = useMyChangeTier();
  const myRemoveMod = useMyRemoveModule();
  const myUpdateUC = useMyUpdateModuleUserCount();
  const myConvertTrial = useMyConvertTrial();
  const myToggleFeat = useMyToggleModuleFeature();

  const createSubscription = isOrgAdmin ? myCreateSub : adminCreateSub;
  const updateSubscription = isOrgAdmin ? myUpdateSub : adminUpdateSub;
  const addModule = isOrgAdmin ? myAddMod : adminAddMod;
  const changeTier = isOrgAdmin ? myChangeTier : adminChangeTier;
  const removeModule = isOrgAdmin ? myRemoveMod : adminRemoveMod;
  const updateUserCount = isOrgAdmin ? myUpdateUC : adminUpdateUC;
  const convertTrial = isOrgAdmin ? myConvertTrial : adminConvertTrial;
  const toggleFeature = isOrgAdmin ? myToggleFeat : adminToggleFeat;

  // State
  const [showAddModule, setShowAddModule] = useState(false);
  const [showEditSubscription, setShowEditSubscription] = useState(false);
  const [moduleToRemove, setModuleToRemove] = useState<string | null>(null);
  const [moduleToConvert, setModuleToConvert] = useState<string | null>(null);
  const [expandedFeatures, setExpandedFeatures] = useState<Record<string, boolean>>({});
  const [pendingChanges, setPendingChanges] = useState<Record<string, { tierCode?: string; userCount?: number }>>({});
  const [newModuleSelections, setNewModuleSelections] = useState<ModuleSelectionItem[]>([]);
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY');
  const [currency, setCurrency] = useState('USD');
  const [tierDowngradeImpact, setTierDowngradeImpact] = useState<{
    moduleCode: string;
    tierCode: string;
    affectedAddons: FeatureAddonItem[];
  } | null>(null);
  const [checkingImpact, setCheckingImpact] = useState(false);
  const [featureGateDialog, setFeatureGateDialog] = useState<{
    open: boolean;
    moduleCode: string;
    featureCode: string;
    featureName: string;
    enabling: boolean;
    impact: FeatureImpactDto | null;
    accessUntilDate?: string;
  } | null>(null);
  const [featureImpactLoading, setFeatureImpactLoading] = useState(false);
  const [featureDialogConfirming, setFeatureDialogConfirming] = useState(false);

  // OrgAdmin feature toggle with billing confirmation
  const handleOrgAdminFeatureToggle = async (
    moduleCode: string,
    feature: string,
    featureName: string,
    isCurrentlyEnabled: boolean,
    periodEnd?: string,
  ) => {
    if (!isCurrentlyEnabled) {
      setFeatureImpactLoading(true);
      try {
        const impact = await addonService.getAddonImpact(moduleCode, feature);
        setFeatureGateDialog({
          open: true, moduleCode, featureCode: feature,
          featureName: featureName || feature, enabling: true, impact,
        });
      } catch (err) {
        const { message } = isApiError(err)
          ? formatErrorForDisplay(err, t)
          : { message: t('subscriptionManagement.featureToggle.impactFetchFailed', 'Failed to load feature details') };
        showError(message);
      } finally {
        setFeatureImpactLoading(false);
      }
    } else {
      setFeatureGateDialog({
        open: true, moduleCode, featureCode: feature,
        featureName: featureName || feature, enabling: false,
        impact: null, accessUntilDate: periodEnd,
      });
    }
  };

  const handleFeatureDialogConfirm = async () => {
    if (!featureGateDialog) return;
    const { moduleCode, featureCode, enabling } = featureGateDialog;
    setFeatureDialogConfirming(true);
    try {
      await toggleFeature.mutateAsync({
        orgId: id || '',
        moduleCode,
        featureCode,
        enabled: enabling,
      });
      await refetchSubscription();
      success(enabling
        ? t('subscriptionManagement.featureToggle.enabledSuccess', 'Feature enabled successfully')
        : t('subscriptionManagement.featureToggle.disabledSuccess', 'Feature disabled — access continues until end of billing period'));
    } catch (err) {
      const { message } = isApiError(err)
        ? formatErrorForDisplay(err, t)
        : { message: t('subscriptionManagement.featureToggle.toggleFailed', 'Failed to toggle feature') };
      showError(message);
    } finally {
      setFeatureDialogConfirming(false);
      setFeatureGateDialog(null);
    }
  };

  // Check if subscription exists (subscriptionId is returned directly on the DTO)
  const hasSubscription = !!subscription?.subscriptionId;

  // Group tiers by module code
  const tiersByModule = useMemo(() => {
    const grouped: Record<string, ModulePricingTier[]> = {};
    pricingTiers.filter(t => t.isActive && t.isPublic).forEach(tier => {
      if (!grouped[tier.moduleCode]) {
        grouped[tier.moduleCode] = [];
      }
      grouped[tier.moduleCode].push(tier);
    });
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return grouped;
  }, [pricingTiers]);

  // Get available modules (not already subscribed, hide coming-soon for OrgAdmin)
  const availableModules = useMemo(() => {
    const subscribedCodes = subscription?.modules?.map(m => m.moduleCode) || [];
    return Object.keys(tiersByModule).filter(
      code => !subscribedCodes.includes(code) && code !== 'CORE'
        && !(isOrgAdmin && COMING_SOON_MODULES.has(code))
    );
  }, [tiersByModule, subscription, isOrgAdmin]);

  // Initialize new module selections when showing add modal, edit modal, or when no subscription exists
  useEffect(() => {
    // Initialize when:
    // 1. Adding modules to existing subscription (showAddModule is true)
    // 2. Creating new subscription (no subscription exists and tiers are loaded)
    // 3. Editing existing subscription (showEditSubscription is true)
    const shouldInitialize = showAddModule || showEditSubscription || (!hasSubscription && Object.keys(tiersByModule).length > 0);

    if (shouldInitialize) {
      let modulesToShow: string[];

      if (showEditSubscription && subscription?.modules) {
        // For editing, show all modules (not just available ones) and pre-select current ones
        modulesToShow = Object.keys(tiersByModule).filter(code => code !== 'CORE');
        const currentModuleCodes = new Set(subscription.modules.map(m => m.moduleCode));

        const initialSelections: ModuleSelectionItem[] = modulesToShow.map(moduleCode => {
          const tiers = tiersByModule[moduleCode];
          const currentModule = subscription.modules.find(m => m.moduleCode === moduleCode);
          const defaultTier = currentModule
            ? tiers.find(t => t.tierCode === currentModule.tierCode) || tiers[0]
            : tiers.find(t => t.tierCode === 'PRO') || tiers[0];
          return {
            moduleCode,
            tierCode: defaultTier?.tierCode || 'BASIC',
            userCount: currentModule?.userCount || 5,
            selected: currentModuleCodes.has(moduleCode),
          };
        });
        setNewModuleSelections(initialSelections);
      } else {
        // For new subscriptions or adding modules, use previous logic
        modulesToShow = hasSubscription ? availableModules : Object.keys(tiersByModule).filter(code => code !== 'CORE');

        if (modulesToShow.length > 0) {
          const initialSelections: ModuleSelectionItem[] = modulesToShow.map(moduleCode => {
            const tiers = tiersByModule[moduleCode];
            const defaultTier = tiers.find(t => t.tierCode === 'PRO') || tiers[0];
            return {
              moduleCode,
              tierCode: defaultTier?.tierCode || 'BASIC',
              userCount: 5,
              selected: false,
            };
          });
          setNewModuleSelections(initialSelections);
        }
      }
    }
  }, [showAddModule, showEditSubscription, availableModules, tiersByModule, hasSubscription, subscription?.modules]);

  // Set defaults from subscription
  useEffect(() => {
    if (subscription?.subscriptionId) {
      setBillingCycle(subscription.billingCycle);
      setCurrency(subscription.currency);
    }
  }, [subscription]);

  // Back navigation handler - invalidates queries to refresh OrganizationDetailPage
  const handleBackNavigation = useCallback(() => {
    if (isOrgAdmin) {
      navigate('/venta/my-organization');
      return;
    }
    if (id) {
      // Invalidate organization, subscription, and invoice queries to ensure fresh data on the detail page
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.org(id) });
      // Invalidate all org invoices (recent invoices section shows updated amounts)
      queryClient.invalidateQueries({ queryKey: ['invoices', 'org', id] });
    }
    navigate(`/superadmin/organizations/${id}`);
  }, [id, isOrgAdmin, queryClient, navigate]);

  // Handlers
  const handleTierChange = (moduleCode: string, tierCode: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [moduleCode]: { ...prev[moduleCode], tierCode },
    }));
  };

  const handleUserCountChange = (moduleCode: string, userCount: number) => {
    setPendingChanges(prev => ({
      ...prev,
      [moduleCode]: { ...prev[moduleCode], userCount: Math.max(1, userCount) },
    }));
  };

  const executeTierChange = async (moduleCode: string, tierCode: string) => {
    if (!id) return;
    try {
      await changeTier.mutateAsync({
        orgId: id,
        moduleCode,
        data: { tierCode },
      });
      success(t('subscriptionManagement.messages.tierChanged', 'Tier changed successfully'));
      setPendingChanges(prev => {
        const updated = { ...prev };
        delete updated[moduleCode]?.tierCode;
        if (!updated[moduleCode]?.userCount) delete updated[moduleCode];
        return updated;
      });
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.tierChangeFailed', 'Failed to change tier') };
      showError(message);
    }
  };

  const saveTierChange = async (moduleCode: string) => {
    const change = pendingChanges[moduleCode];
    if (!change?.tierCode || !id) return;

    // Check for add-on impact before proceeding
    setCheckingImpact(true);
    try {
      const affectedAddons = isOrgAdmin
        ? await mySubscriptionService.previewTierChangeImpact(moduleCode, change.tierCode)
        : await subscriptionsService.previewTierChangeImpact(id, moduleCode, change.tierCode);

      if (affectedAddons.length > 0) {
        // Show confirmation dialog with affected add-ons
        setTierDowngradeImpact({ moduleCode, tierCode: change.tierCode, affectedAddons });
      } else {
        // No impact — proceed directly
        await executeTierChange(moduleCode, change.tierCode);
      }
    } catch (err) {
      // If preview fails, still allow the change (the backend will handle it)
      await executeTierChange(moduleCode, change.tierCode);
    } finally {
      setCheckingImpact(false);
    }
  };

  const saveUserCountChange = async (moduleCode: string) => {
    const change = pendingChanges[moduleCode];
    if (!change?.userCount || !id) return;

    try {
      await updateUserCount.mutateAsync({
        orgId: id,
        moduleCode,
        userCount: change.userCount,
      });
      success(t('subscriptionManagement.messages.userCountUpdated', 'User count updated'));
      setPendingChanges(prev => {
        const updated = { ...prev };
        delete updated[moduleCode]?.userCount;
        if (!updated[moduleCode]?.tierCode) delete updated[moduleCode];
        return updated;
      });
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.userCountFailed', 'Failed to update user count') };
      showError(message);
    }
  };

  const handleRemoveModule = async () => {
    if (!moduleToRemove || !id) return;

    try {
      await removeModule.mutateAsync({
        orgId: id,
        moduleCode: moduleToRemove,
        reason: 'Removed by SuperAdmin',
      });
      success(t('subscriptionManagement.messages.moduleRemoved', 'Module removed'));
      // Close modal first, then refetch
      setModuleToRemove(null);
      // Refetch to update the UI with the removed module
      await refetchSubscription();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.moduleRemoveFailed', 'Failed to remove module') };
      showError(message);
      // Keep modal open on error so user can see what failed
    }
  };

  const handleConvertTrial = async () => {
    if (!moduleToConvert || !id) return;

    try {
      await convertTrial.mutateAsync({
        orgId: id,
        moduleCode: moduleToConvert,
      });
      success(t('subscriptionManagement.messages.trialConverted', 'Trial converted to active'));
      setModuleToConvert(null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.trialConvertFailed', 'Failed to convert trial') };
      showError(message);
    }
  };

  const handleAddSelectedModules = async () => {
    if (!id) return;

    const selectedModules = newModuleSelections.filter(m => m.selected);
    if (selectedModules.length === 0) {
      showError(t('subscriptionManagement.messages.selectModule', 'Please select at least one module'));
      return;
    }

    try {
      for (const module of selectedModules) {
        await addModule.mutateAsync({
          orgId: id,
          data: {
            moduleCode: module.moduleCode,
            tierCode: module.tierCode,
            userCount: module.userCount,
            startTrial: true,
          },
        });
      }
      success(t('subscriptionManagement.messages.modulesAdded', 'Modules added successfully'));
      setShowAddModule(false);
      setNewModuleSelections([]);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.addModuleFailed', 'Failed to add modules') };
      showError(message);
    }
  };

  const handleCreateSubscription = async () => {
    if (!id) return;

    const selectedModules = newModuleSelections.filter(m => m.selected);
    if (selectedModules.length === 0) {
      showError(t('subscriptionManagement.messages.selectModule', 'Please select at least one module'));
      return;
    }

    const subscriptionData = {
      modules: selectedModules.map(m => ({
        moduleCode: m.moduleCode,
        tierCode: m.tierCode,
        userCount: m.userCount,
      })),
      billingCycle,
      currency,
    };

    try {
      await createSubscription.mutateAsync({
        orgId: id,
        data: subscriptionData,
      });
      success(t('subscriptionManagement.messages.subscriptionCreated', 'Subscription created successfully!'));
      // Clear selections and refetch subscription data
      setNewModuleSelections([]);
      setShowAddModule(false);
      // Force refetch to show the new subscription
      await refetchSubscription();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.createFailed', 'Failed to create subscription') };
      showError(message);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!id) return;

    const selectedModules = newModuleSelections.filter(m => m.selected);
    if (selectedModules.length === 0) {
      showError(t('subscriptionManagement.messages.selectModule', 'Please select at least one module'));
      return;
    }

    const subscriptionData = {
      modules: selectedModules.map(m => ({
        moduleCode: m.moduleCode,
        tierCode: m.tierCode,
        userCount: m.userCount,
      })),
      billingCycle,
      currency,
    };

    try {
      await updateSubscription.mutateAsync({
        orgId: id,
        data: subscriptionData,
      });
      success(t('subscriptionManagement.messages.subscriptionUpdated', 'Subscription updated successfully!'));
      // Clear selections and close the edit modal
      setNewModuleSelections([]);
      setShowEditSubscription(false);
      // Force refetch to show the updated subscription
      await refetchSubscription();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('subscriptionManagement.messages.updateFailed', 'Failed to update subscription') };
      showError(message);
    }
  };

  // Calculate pricing for new modules
  const newModulePricing = useMemo(() => {
    const selectedModules = newModuleSelections.filter(ms => ms.selected);
    const isAnnual = billingCycle === 'ANNUAL';

    let subtotal = 0;
    selectedModules.forEach(ms => {
      const tiers = tiersByModule[ms.moduleCode];
      const tier = tiers?.find(t => t.tierCode === ms.tierCode);
      if (!tier) return;

      if (tier.pricingModel === 'PER_USER') {
        const unitPrice = isAnnual ? tier.perUserAnnualUsd / 12 : tier.perUserMonthlyUsd;
        subtotal += unitPrice * ms.userCount;
      } else {
        subtotal += isAnnual ? tier.annualPriceUsd / 12 : tier.monthlyPriceUsd;
      }
    });

    return { subtotal, moduleCount: selectedModules.length };
  }, [newModuleSelections, billingCycle, tiersByModule]);

  const isLoading = orgLoading || subLoading;
  const isSaving = createSubscription.isPending || updateSubscription.isPending || addModule.isPending ||
    changeTier.isPending || removeModule.isPending || updateUserCount.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!org && !isOrgAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted">
        <Building2 className="w-12 h-12 mb-3 opacity-50" />
        <p>{t('organizationDetail.notFound', 'Organization not found')}</p>
        <button
          onClick={() => navigate('/superadmin/organizations')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm"
        >
          {t('common.backToList', 'Back to List')}
        </button>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackNavigation}
            className="p-2 rounded-lg border border-border hover:bg-background transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              <Building2 className="w-5 h-5 inline mr-2" />
              {t('subscriptionManagement.title', 'Manage Subscription')}
            </h1>
            <p className="page-subtitle">{org?.name || user?.orgName || ''}{org?.code ? ` - ${org.code}` : ''}</p>
          </div>
        </div>
        <div className="page-actions">
          <button onClick={() => refetchSubscription()} className="btn btn-secondary">
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh', 'Refresh')}
          </button>
          {hasSubscription && (
            <>
              <button onClick={() => setShowEditSubscription(true)} className="btn btn-secondary">
                <Edit className="w-4 h-4" />
                {t('subscriptionManagement.editPlan', 'Edit Plan')}
              </button>
              {availableModules.length > 0 && (
                <button onClick={() => setShowAddModule(true)} className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  {t('subscriptionManagement.addModule', 'Add Module')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* No Subscription State */}
      {!hasSubscription && (
        <div className="no-subscription-card">
          <AlertCircle className="w-12 h-12 text-warning mb-4" />
          <h2>{t('subscriptionManagement.noSubscription', 'No Modular Subscription')}</h2>
          <p>{t('subscriptionManagement.noSubscriptionDesc', 'This organization does not have a modular subscription yet. Create one to enable module-based billing.')}</p>

          {/* Billing Settings */}
          <div className="billing-settings">
            <div className="billing-option">
              <label>{t('subscriptionManagement.billingCycle', 'Billing Cycle')}</label>
              <select
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value as 'MONTHLY' | 'ANNUAL')}
                className="form-select"
              >
                <option value="MONTHLY">{t('common.monthly', 'Monthly')}</option>
                <option value="ANNUAL">{t('common.annual', 'Annual')}</option>
              </select>
            </div>
            <div className="billing-option">
              <label>{t('subscriptionManagement.currency', 'Currency')}</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="form-select"
              >
                {currencies.map(c => (
                  <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Module Selection for New Subscription */}
          <div className="module-selection-grid">
            {Object.keys(tiersByModule).filter(code => code !== 'CORE' && !(isOrgAdmin && COMING_SOON_MODULES.has(code))).map(moduleCode => {
              const moduleInfo = MODULE_INFO[moduleCode] || { name: moduleCode, icon: '📦', description: '' };
              const moduleTiers = tiersByModule[moduleCode] || [];
              const selection = newModuleSelections.find(m => m.moduleCode === moduleCode);
              const selectedTier = moduleTiers.find(t => t.tierCode === selection?.tierCode);
              const isPerUser = selectedTier?.pricingModel === 'PER_USER';

              return (
                <div key={moduleCode} className={`module-card ${selection?.selected ? 'selected' : ''}`}>
                  <div className="module-header">
                    <label className="module-checkbox">
                      <input
                        type="checkbox"
                        checked={selection?.selected || false}
                        onChange={() => {
                          setNewModuleSelections(prev =>
                            prev.map(m =>
                              m.moduleCode === moduleCode ? { ...m, selected: !m.selected } : m
                            )
                          );
                        }}
                      />
                      <span className="checkmark">
                        {selection?.selected && <Check className="w-4 h-4" />}
                      </span>
                    </label>
                    <div className="module-info">
                      <span className="module-icon">{moduleInfo.icon}</span>
                      <div>
                        <div className="module-name">{moduleInfo.name}</div>
                        <div className="module-desc">{moduleInfo.description}</div>
                      </div>
                    </div>
                  </div>

                  {selection?.selected && (
                    <div className="module-config">
                      <div className="config-row">
                        <label>{t('common.tier', 'Tier')}</label>
                        <select
                          value={selection.tierCode}
                          onChange={(e) => {
                            setNewModuleSelections(prev =>
                              prev.map(m =>
                                m.moduleCode === moduleCode ? { ...m, tierCode: e.target.value } : m
                              )
                            );
                          }}
                          className="form-select"
                        >
                          {moduleTiers.map(tier => (
                            <option key={tier.tierCode} value={tier.tierCode}>
                              {tier.tierName} - ${tier.pricingModel === 'PER_USER' ? tier.perUserMonthlyUsd + '/user' : tier.monthlyPriceUsd}/mo
                            </option>
                          ))}
                        </select>
                      </div>
                      {isPerUser && (
                        <div className="config-row">
                          <label>{t('common.users', 'Users')}</label>
                          <input
                            type="number"
                            min="1"
                            value={selection.userCount}
                            onChange={(e) => {
                              setNewModuleSelections(prev =>
                                prev.map(m =>
                                  m.moduleCode === moduleCode ? { ...m, userCount: Math.max(1, parseInt(e.target.value) || 1) } : m
                                )
                              );
                            }}
                            className="form-input user-count-input"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pricing Summary */}
          {newModulePricing.moduleCount > 0 && (
            <div className="pricing-summary">
              <div className="pricing-row">
                <span>{t('common.subtotal', 'Subtotal')}</span>
                <span>${newModulePricing.subtotal.toFixed(2)}/mo</span>
              </div>
              <div className="pricing-total">
                <span>{t('common.total', 'Total')}</span>
                <span>${newModulePricing.subtotal.toFixed(2)}/mo</span>
              </div>
            </div>
          )}

          <button
            onClick={handleCreateSubscription}
            disabled={newModulePricing.moduleCount === 0 || isSaving}
            className="btn btn-primary btn-lg"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t('subscriptionManagement.createSubscription', 'Create Subscription')}
          </button>
        </div>
      )}

      {/* Existing Subscription */}
      {hasSubscription && subscription && (
        <>
          {/* Subscription Summary */}
          <div className="subscription-summary">
            <div className="summary-stat">
              <DollarSign className="w-5 h-5" />
              <div>
                <div className="stat-value">${subscription.totalUsd?.toFixed(2) || '0.00'}</div>
                <div className="stat-label">{t('subscriptionManagement.monthlyTotal', 'Monthly Total (USD)')}</div>
              </div>
            </div>
            <div className="summary-stat">
              <Users className="w-5 h-5" />
              <div>
                <div className="stat-value">{subscription.totalModules}</div>
                <div className="stat-label">{t('subscriptionManagement.activeModules', 'Active Modules')}</div>
              </div>
            </div>
            {subscription.discountPercent && subscription.discountPercent > 0 && (
              <div className="summary-stat discount">
                <Percent className="w-5 h-5" />
                <div>
                  <div className="stat-value">{subscription.discountPercent}%</div>
                  <div className="stat-label">{subscription.appliedDiscountName || t('subscriptionManagement.volumeDiscount', 'Volume Discount')}</div>
                </div>
              </div>
            )}
          </div>

          {/* Module List */}
          <div className="modules-section">
            <h2>{t('subscriptionManagement.subscribedModules', 'Subscribed Modules')}</h2>
            <div className="modules-list">
              {subscription.modules?.filter((module: ModuleSubscriptionItem) =>
                !(isOrgAdmin && (COMING_SOON_MODULES.has(module.moduleCode) || module.moduleCode === 'CORE'))
              ).map((module: ModuleSubscriptionItem) => {
                const moduleInfo = MODULE_INFO[module.moduleCode] || { name: module.moduleCode, icon: '📦', description: '' };
                const moduleTiers = tiersByModule[module.moduleCode] || [];
                const isPerUser = module.pricingModel === 'PER_USER';
                const pendingChange = pendingChanges[module.moduleCode];
                const isCore = module.moduleCode === 'CORE';

                return (
                  <div key={module.moduleCode} className={`module-item ${module.isTrial ? 'trial' : ''}`}>
                    <div className="module-main">
                      <span className="module-icon">{moduleInfo.icon}</span>
                      <div className="module-details">
                        <div className="module-name">
                          {moduleInfo.name}
                          {module.isTrial && (
                            <span className="trial-badge">{t('common.trial', 'Trial')}</span>
                          )}
                        </div>
                        <div className="module-tier">{module.tierName || module.tierCode}</div>
                      </div>
                    </div>

                    <div className="module-config-inline">
                      {/* Tier Selector */}
                      {!isCore && moduleTiers.length > 1 && (
                        <div className="config-field">
                          <select
                            value={pendingChange?.tierCode || module.tierCode}
                            onChange={(e) => handleTierChange(module.moduleCode, e.target.value)}
                            className="form-select"
                          >
                            {moduleTiers.map(tier => (
                              <option key={tier.tierCode} value={tier.tierCode}>
                                {tier.tierName}
                              </option>
                            ))}
                          </select>
                          {pendingChange?.tierCode && pendingChange.tierCode !== module.tierCode && (
                            <button
                              onClick={() => saveTierChange(module.moduleCode)}
                              disabled={changeTier.isPending || checkingImpact}
                              className="btn btn-sm btn-primary"
                            >
                              {changeTier.isPending || checkingImpact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </button>
                          )}
                        </div>
                      )}

                      {/* User Count */}
                      {isPerUser && (
                        <div className="config-field">
                          <div className="user-count-field">
                            <Users className="w-4 h-4" />
                            <input
                              type="number"
                              min="1"
                              value={pendingChange?.userCount ?? module.userCount ?? 1}
                              onChange={(e) => handleUserCountChange(module.moduleCode, parseInt(e.target.value) || 1)}
                              className="form-input user-count-input"
                            />
                          </div>
                          {pendingChange?.userCount && pendingChange.userCount !== module.userCount && (
                            <button
                              onClick={() => saveUserCountChange(module.moduleCode)}
                              disabled={updateUserCount.isPending}
                              className="btn btn-sm btn-primary"
                            >
                              {updateUserCount.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Price */}
                      <div className="module-price">
                        ${module.lineTotalUsd?.toFixed(2) || '0.00'}/mo
                      </div>

                      {/* Actions */}
                      {!isCore && (
                        <div className="module-actions">
                          {module.isTrial && (
                            <button
                              onClick={() => setModuleToConvert(module.moduleCode)}
                              className="btn btn-sm btn-success"
                              title={t('subscriptionManagement.convertTrial', 'Convert to Active')}
                            >
                              <Check className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={() => setModuleToRemove(module.moduleCode)}
                            className="btn btn-sm btn-danger"
                            title={t('common.remove', 'Remove')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Feature Toggles */}
                    {module.availableFeatures && module.availableFeatures.length > 0 && (
                      <div className="module-features">
                        <button
                          className="features-toggle-btn"
                          onClick={() => setExpandedFeatures(prev => ({
                            ...prev,
                            [module.moduleCode]: !prev[module.moduleCode]
                          }))}
                        >
                          {expandedFeatures[module.moduleCode] ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                          <span>Features ({Object.values(module.enabledFeatures || {}).filter(Boolean).length}/{module.availableFeatures.length})</span>
                        </button>

                        {expandedFeatures[module.moduleCode] && (
                          <div className="features-list">
                            {module.availableFeatures.map((feature) => {
                              const isEnabled = module.enabledFeatures?.[feature] ?? false;
                              const featureDisplayNames: Record<string, string> = { WMS: 'Warehouse Operations (WMS)' };
                              const label = featureDisplayNames[feature] ?? feature.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                              return (
                                <div key={feature} className="feature-row">
                                  <span className="feature-label">{label}</span>
                                  <button
                                    className={`feature-toggle ${isEnabled ? 'enabled' : 'disabled'}`}
                                    onClick={() => {
                                      if (!id || isOrgAdmin) return;
                                      toggleFeature.mutate({
                                        orgId: id,
                                        moduleCode: module.moduleCode,
                                        featureCode: feature,
                                        enabled: !isEnabled,
                                      });
                                    }}
                                    disabled={isOrgAdmin || toggleFeature.isPending || featureImpactLoading}
                                    title={isEnabled ? 'Disable' : 'Enable'}
                                  >
                                    {isEnabled ? (
                                      <ToggleRight className="w-5 h-5" />
                                    ) : (
                                      <ToggleLeft className="w-5 h-5" />
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Feature Add-Ons Section */}
          {subscription && (
            <div className="smp-addons-section">
              <h3 className="smp-addons-title">
                <Puzzle size={18} />
                {t('addons.marketplace.title')}
              </h3>
              <AddonsMarketplace />
            </div>
          )}
        </>
      )}

      {/* Add Module Modal */}
      {showAddModule && hasSubscription && (
        <div className="modal-overlay" onClick={() => setShowAddModule(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{t('subscriptionManagement.addModules', 'Add Modules')}</h2>
            <div className="module-selection-grid">
              {availableModules.map(moduleCode => {
                const moduleInfo = MODULE_INFO[moduleCode] || { name: moduleCode, icon: '📦', description: '' };
                const moduleTiers = tiersByModule[moduleCode] || [];
                const selection = newModuleSelections.find(m => m.moduleCode === moduleCode);
                const selectedTier = moduleTiers.find(t => t.tierCode === selection?.tierCode);
                const isPerUser = selectedTier?.pricingModel === 'PER_USER';

                return (
                  <div key={moduleCode} className={`module-card ${selection?.selected ? 'selected' : ''}`}>
                    <div className="module-header">
                      <label className="module-checkbox">
                        <input
                          type="checkbox"
                          checked={selection?.selected || false}
                          onChange={() => {
                            setNewModuleSelections(prev =>
                              prev.map(m =>
                                m.moduleCode === moduleCode ? { ...m, selected: !m.selected } : m
                              )
                            );
                          }}
                        />
                        <span className="checkmark">
                          {selection?.selected && <Check className="w-4 h-4" />}
                        </span>
                      </label>
                      <div className="module-info">
                        <span className="module-icon">{moduleInfo.icon}</span>
                        <div>
                          <div className="module-name">{moduleInfo.name}</div>
                          <div className="module-desc">{moduleInfo.description}</div>
                        </div>
                      </div>
                    </div>

                    {selection?.selected && (
                      <div className="module-config">
                        <div className="config-row">
                          <label>{t('common.tier', 'Tier')}</label>
                          <select
                            value={selection.tierCode}
                            onChange={(e) => {
                              setNewModuleSelections(prev =>
                                prev.map(m =>
                                  m.moduleCode === moduleCode ? { ...m, tierCode: e.target.value } : m
                                )
                              );
                            }}
                            className="form-select"
                          >
                            {moduleTiers.map(tier => (
                              <option key={tier.tierCode} value={tier.tierCode}>
                                {tier.tierName} - ${tier.pricingModel === 'PER_USER' ? tier.perUserMonthlyUsd + '/user' : tier.monthlyPriceUsd}/mo
                              </option>
                            ))}
                          </select>
                        </div>
                        {isPerUser && (
                          <div className="config-row">
                            <label>{t('common.users', 'Users')}</label>
                            <input
                              type="number"
                              min="1"
                              value={selection.userCount}
                              onChange={(e) => {
                                setNewModuleSelections(prev =>
                                  prev.map(m =>
                                    m.moduleCode === moduleCode ? { ...m, userCount: Math.max(1, parseInt(e.target.value) || 1) } : m
                                  )
                                );
                              }}
                              className="form-input user-count-input"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowAddModule(false)} className="btn btn-secondary">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleAddSelectedModules}
                disabled={newModulePricing.moduleCount === 0 || isSaving}
                className="btn btn-primary"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t('subscriptionManagement.addSelected', 'Add Selected')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subscription Modal */}
      {showEditSubscription && hasSubscription && (
        <div className="modal-overlay" onClick={() => setShowEditSubscription(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <h2>{t('subscriptionManagement.editSubscription', 'Edit Subscription')}</h2>
            <p className="modal-description">{t('subscriptionManagement.editSubscriptionDesc', 'Select the modules you want and update the subscription.')}</p>

            {/* Billing Settings */}
            <div className="billing-settings" style={{ marginBottom: '16px' }}>
              <div className="billing-option">
                <label>{t('subscriptionManagement.billingCycle', 'Billing Cycle')}</label>
                <select
                  value={billingCycle}
                  onChange={(e) => setBillingCycle(e.target.value as 'MONTHLY' | 'ANNUAL')}
                  className="form-select"
                >
                  <option value="MONTHLY">{t('common.monthly', 'Monthly')}</option>
                  <option value="ANNUAL">{t('common.annual', 'Annual')}</option>
                </select>
              </div>
              <div className="billing-option">
                <label>{t('subscriptionManagement.currency', 'Currency')}</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="form-select"
                >
                  {currencies.map(c => (
                    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="module-selection-grid">
              {Object.keys(tiersByModule).filter(code => code !== 'CORE' && !(isOrgAdmin && COMING_SOON_MODULES.has(code))).map(moduleCode => {
                const moduleInfo = MODULE_INFO[moduleCode] || { name: moduleCode, icon: '📦', description: '' };
                const moduleTiers = tiersByModule[moduleCode] || [];
                const selection = newModuleSelections.find(m => m.moduleCode === moduleCode);
                const selectedTier = moduleTiers.find(t => t.tierCode === selection?.tierCode);
                const isPerUser = selectedTier?.pricingModel === 'PER_USER';

                return (
                  <div key={moduleCode} className={`module-card ${selection?.selected ? 'selected' : ''}`}>
                    <div className="module-header">
                      <label className="module-checkbox">
                        <input
                          type="checkbox"
                          checked={selection?.selected || false}
                          onChange={() => {
                            setNewModuleSelections(prev =>
                              prev.map(m =>
                                m.moduleCode === moduleCode ? { ...m, selected: !m.selected } : m
                              )
                            );
                          }}
                        />
                        <span className="checkmark">
                          {selection?.selected && <Check className="w-4 h-4" />}
                        </span>
                      </label>
                      <div className="module-info">
                        <span className="module-icon">{moduleInfo.icon}</span>
                        <div>
                          <div className="module-name">{moduleInfo.name}</div>
                          <div className="module-desc">{moduleInfo.description}</div>
                        </div>
                      </div>
                    </div>

                    {selection?.selected && (
                      <div className="module-config">
                        <div className="config-row">
                          <label>{t('common.tier', 'Tier')}</label>
                          <select
                            value={selection.tierCode}
                            onChange={(e) => {
                              setNewModuleSelections(prev =>
                                prev.map(m =>
                                  m.moduleCode === moduleCode ? { ...m, tierCode: e.target.value } : m
                                )
                              );
                            }}
                            className="form-select"
                          >
                            {moduleTiers.map(tier => (
                              <option key={tier.tierCode} value={tier.tierCode}>
                                {tier.tierName} - ${tier.pricingModel === 'PER_USER' ? tier.perUserMonthlyUsd + '/user' : tier.monthlyPriceUsd}/mo
                              </option>
                            ))}
                          </select>
                        </div>
                        {isPerUser && (
                          <div className="config-row">
                            <label>{t('common.users', 'Users')}</label>
                            <input
                              type="number"
                              min="1"
                              value={selection.userCount}
                              onChange={(e) => {
                                setNewModuleSelections(prev =>
                                  prev.map(m =>
                                    m.moduleCode === moduleCode ? { ...m, userCount: Math.max(1, parseInt(e.target.value) || 1) } : m
                                  )
                                );
                              }}
                              className="form-input user-count-input"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pricing Summary */}
            {newModulePricing.moduleCount > 0 && (
              <div className="pricing-summary">
                <div className="pricing-row">
                  <span>{t('common.subtotal', 'Subtotal')}</span>
                  <span>${newModulePricing.subtotal.toFixed(2)}/mo</span>
                </div>
                <div className="pricing-total">
                  <span>{t('common.total', 'Total')}</span>
                  <span>${newModulePricing.subtotal.toFixed(2)}/mo</span>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setShowEditSubscription(false)} className="btn btn-secondary">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleUpdateSubscription}
                disabled={newModulePricing.moduleCount === 0 || isSaving}
                className="btn btn-primary"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('subscriptionManagement.updateSubscription', 'Update Subscription')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Module Confirmation */}
      <ConfirmationDialog
        isOpen={!!moduleToRemove}
        onCancel={() => setModuleToRemove(null)}
        onConfirm={handleRemoveModule}
        title={t('subscriptionManagement.removeModuleTitle', 'Remove Module')}
        message={t('subscriptionManagement.removeModuleConfirm', `Are you sure you want to remove the ${MODULE_INFO[moduleToRemove || '']?.name || moduleToRemove} module?`)}
        confirmText={t('common.remove', 'Remove')}
        variant="danger"
        isLoading={removeModule.isPending}
      />

      {/* Convert Trial Confirmation */}
      <ConfirmationDialog
        isOpen={!!moduleToConvert}
        onCancel={() => setModuleToConvert(null)}
        onConfirm={handleConvertTrial}
        title={t('subscriptionManagement.convertTrialTitle', 'Convert Trial')}
        message={t('subscriptionManagement.convertTrialConfirm', `Convert the ${MODULE_INFO[moduleToConvert || '']?.name || moduleToConvert} trial to an active subscription?`)}
        confirmText={t('subscriptionManagement.convertToActive', 'Convert to Active')}
        variant="approve"
        isLoading={convertTrial.isPending}
      />

      {/* Tier Downgrade Add-on Impact Confirmation */}
      <ConfirmationDialog
        isOpen={!!tierDowngradeImpact}
        onCancel={() => setTierDowngradeImpact(null)}
        onConfirm={async () => {
          if (!tierDowngradeImpact) return;
          await executeTierChange(tierDowngradeImpact.moduleCode, tierDowngradeImpact.tierCode);
          setTierDowngradeImpact(null);
        }}
        title={t('subscriptionManagement.tierDowngradeTitle', 'Tier Downgrade Warning')}
        message={t(
          'subscriptionManagement.tierDowngradeMessage',
          `Downgrading to ${tierDowngradeImpact?.tierCode || ''} will automatically cancel the following add-ons that require a higher tier:`
        )}
        details={
          tierDowngradeImpact && (
            <ul style={{ margin: '0.75rem 0 0', padding: '0 0 0 1.25rem', listStyle: 'disc' }}>
              {tierDowngradeImpact.affectedAddons.map(addon => (
                <li key={addon.id} style={{ marginBottom: '0.375rem', color: 'var(--color-text-primary)' }}>
                  <strong>{addon.addonName || addon.featureCode}</strong>
                  {addon.lineTotalUsd > 0 && (
                    <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.5rem' }}>
                      (${addon.lineTotalUsd.toFixed(2)}/mo)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )
        }
        confirmText={t('subscriptionManagement.confirmDowngrade', 'Downgrade & Remove Add-ons')}
        variant="danger"
        isLoading={changeTier.isPending}
      />

      {featureGateDialog?.open && (
        <FeatureToggleConfirmDialog
          featureCode={featureGateDialog.featureCode}
          featureName={featureGateDialog.featureName}
          moduleCode={featureGateDialog.moduleCode}
          description={featureGateDialog.impact?.description ?? ''}
          enabling={featureGateDialog.enabling}
          currentTotalUsd={featureGateDialog.impact?.currentTotalUsd}
          deltaUsd={featureGateDialog.impact?.monthlyPriceUsd}
          newTotalUsd={featureGateDialog.impact?.newTotalUsd}
          proratedAmountUsd={featureGateDialog.impact?.proratedAmountUsd}
          accessUntilDate={featureGateDialog.accessUntilDate}
          onConfirm={handleFeatureDialogConfirm}
          onCancel={() => setFeatureGateDialog(null)}
          isLoading={featureDialogConfirming}
        />
      )}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .subscription-page {
    width: 100%;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }

  .page-title {
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  .page-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-brand-primary-hover);
  }

  .btn-secondary {
    background: transparent;
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .btn-success {
    background: var(--color-success, #10b981);
    color: white;
  }

  .btn-danger {
    background: var(--color-error, #ef4444);
    color: white;
  }

  .btn-sm {
    padding: 4px 8px;
    font-size: 11px;
  }

  .btn-lg {
    padding: 12px 24px;
    font-size: 15px;
  }

  /* No Subscription Card */
  .no-subscription-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 40px;
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
  }

  .no-subscription-card h2 {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .no-subscription-card p {
    color: var(--color-text-secondary);
    margin-bottom: 24px;
  }

  .billing-settings {
    display: flex;
    gap: 16px;
    justify-content: center;
    margin-bottom: 24px;
  }

  .billing-option {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .billing-option label {
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  /* Subscription Summary */
  .subscription-summary {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .summary-stat {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 10px;
    padding: 16px 20px;
    min-width: 180px;
  }

  .summary-stat.discount {
    background: rgba(var(--color-success-rgb, 16, 185, 129), 0.1);
    border-color: var(--color-success, #10b981);
  }

  .stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .stat-label {
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  /* Modules Section */
  .modules-section {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 20px;
  }

  .modules-section h2 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .modules-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .module-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    padding: 12px 16px;
    gap: 16px;
    flex-wrap: wrap;
  }

  .module-item.trial {
    border-color: var(--color-warning, #f59e0b);
    background: rgba(var(--color-warning-rgb, 245, 158, 11), 0.05);
  }

  .module-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 200px;
  }

  .module-icon {
    font-size: 24px;
  }

  .module-details {
    display: flex;
    flex-direction: column;
  }

  .module-name {
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .module-tier {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  .trial-badge {
    font-size: 10px;
    padding: 2px 6px;
    background: var(--color-warning, #f59e0b);
    color: white;
    border-radius: 4px;
    font-weight: 500;
  }

  .module-config-inline {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .config-field {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .user-count-field {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--color-text-secondary);
  }

  .user-count-input {
    width: 70px;
    text-align: center;
  }

  .module-price {
    font-weight: 600;
    font-size: 14px;
    color: var(--color-brand-primary);
    min-width: 100px;
    text-align: right;
  }

  .module-actions {
    display: flex;
    gap: 4px;
  }

  /* Feature Toggles */
  .module-features {
    border-top: 1px solid var(--color-border-default);
    margin-top: 8px;
    padding-top: 8px;
  }

  .features-toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: var(--color-text-secondary);
    font-size: 0.75rem;
    cursor: pointer;
    padding: 2px 0;
  }

  .features-toggle-btn:hover {
    color: var(--color-text-primary);
  }

  .features-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
    padding-left: 4px;
  }

  .feature-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .feature-label {
    font-size: 0.8rem;
    color: var(--color-text-secondary);
  }

  .feature-toggle {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    transition: color 0.15s;
  }

  .feature-toggle.enabled {
    color: var(--color-success, #22c55e);
  }

  .feature-toggle.disabled {
    color: var(--color-text-muted, #9ca3af);
  }

  .feature-toggle:hover {
    opacity: 0.8;
  }

  .feature-toggle:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Module Selection Grid */
  .module-selection-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;
    text-align: left;
  }

  @media (max-width: 768px) {
    .module-selection-grid {
      grid-template-columns: 1fr;
    }
  }

  .module-card {
    background: var(--color-bg-primary);
    border: 2px solid var(--color-border-default);
    border-radius: 10px;
    padding: 16px;
    transition: all 0.2s ease;
  }

  .module-card.selected {
    border-color: var(--color-brand-primary);
    background: rgba(var(--color-brand-primary-rgb, 139, 0, 0), 0.05);
  }

  .module-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .module-checkbox {
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
  }

  .module-checkbox input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .module-checkbox .checkmark {
    width: 22px;
    height: 22px;
    border: 2px solid var(--color-border-default);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  .module-card.selected .module-checkbox .checkmark {
    background: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
    color: white;
  }

  .module-info {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .module-desc {
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  .module-config {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .config-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .config-row label {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  /* Pricing Summary */
  .pricing-summary {
    background: var(--color-bg-tertiary);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
  }

  .pricing-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    margin-bottom: 8px;
  }

  .pricing-total {
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 15px;
    border-top: 1px solid var(--color-border-default);
    padding-top: 8px;
  }

  /* Form Elements */
  .form-select, .form-input {
    padding: 6px 10px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    font-size: 13px;
    color: var(--color-text-primary);
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal-content {
    background: var(--color-bg-secondary);
    border-radius: 12px;
    padding: 24px;
    max-width: 700px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
  }

  .modal-content.modal-lg {
    max-width: 900px;
  }

  .modal-content h2 {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .modal-description {
    color: var(--color-text-secondary);
    font-size: 13px;
    margin-bottom: 16px;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
  }

  /* Feature Add-Ons Section */
  .smp-addons-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--color-border-subtle);
  }
  .smp-addons-title {
    display: flex; align-items: center; gap: 8px;
    font-family: var(--font-heading);
    font-size: 1.1rem; font-weight: 600;
    color: var(--color-text-primary);
    margin: 0 0 1rem 0;
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
    }

    .subscription-summary {
      flex-direction: column;
    }

    .module-item {
      flex-direction: column;
      align-items: stretch;
    }

    .module-config-inline {
      justify-content: space-between;
    }
  }
`;
